import { endOfDay, isSameDay, parseISO } from 'date-fns';
import { resolveDisplayName } from './usersDirectory';

export const REMINDER_STATUS_LABELS = {
  pending: 'Pending',
  completed: 'Completed',
  dismissed: 'Dismissed',
};

export const LEADERSHIP_ROLES = new Set(['manager', 'admin', 'super_admin']);

export function isReminderLeadership(role) {
  return LEADERSHIP_ROLES.has(role);
}

export function canDeleteReminder(role, reminder, viewerId) {
  if (!reminder) return false;
  if (reminder.created_by === viewerId) return true;
  return isReminderLeadership(role);
}

export function canEditReminder(role, reminder, viewerId) {
  if (!reminder) return false;
  if (reminder.assigned_to === viewerId || reminder.created_by === viewerId) return true;
  return isReminderLeadership(role);
}

export function reminderAssigneeName(row) {
  return resolveDisplayName(row?.assigned_to, row?.assignee) || '—';
}

export function reminderCreatorName(row) {
  return resolveDisplayName(row?.created_by, row?.creator) || '—';
}

export function reminderLeadName(row) {
  const lead = row?.lead;
  if (!lead) return '';
  return [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
}

export function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromDatetimeLocalValue(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function defaultDueLocalValue() {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 60);
  d.setSeconds(0, 0);
  return toDatetimeLocalValue(d);
}

/**
 * Bucket pending reminders into overdue / today / upcoming; everything else is completed.
 * Overdue includes anything pending whose due_at is already in the past (including earlier today).
 */
export function bucketReminders(rows, now = new Date()) {
  const overdue = [];
  const today = [];
  const upcoming = [];
  const completed = [];
  const endToday = endOfDay(now);

  for (const row of rows || []) {
    if (row.status !== 'pending') {
      completed.push(row);
      continue;
    }
    const due = parseIsoDate(row.due_at);
    if (!due) {
      upcoming.push(row);
      continue;
    }
    if (due.getTime() < now.getTime()) overdue.push(row);
    else if (isSameDay(due, now) || due.getTime() <= endToday.getTime()) today.push(row);
    else upcoming.push(row);
  }

  completed.sort((a, b) => String(b.due_at || '').localeCompare(String(a.due_at || '')));
  return { overdue, today, upcoming, completed };
}

function parseIsoDate(value) {
  if (!value) return null;
  try {
    const d = typeof value === 'string' ? parseISO(value) : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}
